import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAppConfig, putAdminAppConfig } from "./appConfigApi";
import { getProjectGridColumnDescriptors } from "../projectGridColumns";
import {
  COMMERCIAL_TEXT_FILTER_TYPES,
  normalizeCommercialGridDefaults,
} from "./commercialGridFilterDefaults";
import { hydrateCommercialGridDefaultsFromServer } from "./commercialGridDefaultsStorage";
import { loadUserMatrixSettings } from "./userMatrixStorage";
import "./userMatrixPanel.css";

function mkRuleId() {
  return `rule_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function emptyRule(colId = "status") {
  return { id: mkRuleId(), colId, type: "notEqual", filter: "" };
}

function rulesWithIds(rules, fallbackColId = "status") {
  if (rules?.length) return rules.map((r) => ({ ...r, id: mkRuleId() }));
  return [emptyRule(fallbackColId)];
}

function emptyRoleDefault(userTypes) {
  return {
    id: mkRuleId(),
    userTypeId: userTypes[0]?.id || "",
    enabled: true,
    rules: [emptyRule("phase")],
  };
}

function roleDefaultsWithIds(roleDefaults, userTypes) {
  if (roleDefaults?.length) {
    return roleDefaults.map((entry) => ({
      id: mkRuleId(),
      userTypeId: entry.userTypeId,
      enabled: entry.enabled,
      rules: rulesWithIds(entry.rules, "phase"),
    }));
  }
  return [];
}

function FilterRulesTable({ columns, rules, onChange, onAdd, onRemove, addLabel = "Add rule" }) {
  const updateRule = (id, patch) => {
    onChange(rules.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  return (
    <>
      <div className="email-triggers-table-wrap">
        <table className="email-triggers-table">
          <thead>
            <tr>
              <th scope="col">Column</th>
              <th scope="col">Operator</th>
              <th scope="col">Value</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {rules.map((rule) => (
              <tr key={rule.id}>
                <td>
                  <select
                    className="user-matrix-select"
                    value={rule.colId}
                    onChange={(e) => updateRule(rule.id, { colId: e.target.value })}
                    aria-label="Filter column"
                  >
                    {columns.map((c) => (
                      <option key={c.colId} value={c.colId}>
                        {c.headerName}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    className="user-matrix-select"
                    value={rule.type}
                    onChange={(e) => updateRule(rule.id, { type: e.target.value })}
                    aria-label="Filter operator"
                  >
                    {COMMERCIAL_TEXT_FILTER_TYPES.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    className="email-triggers-input"
                    value={rule.filter}
                    onChange={(e) => updateRule(rule.id, { filter: e.target.value })}
                    placeholder="Filter value"
                    aria-label="Filter value"
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="email-triggers-remove"
                    onClick={() => onRemove(rule.id)}
                    disabled={rules.length <= 1}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="user-matrix-invite-row" style={{ marginTop: "0.5rem" }}>
        <button type="button" className="email-triggers-button" onClick={onAdd}>
          {addLabel}
        </button>
      </div>
    </>
  );
}

export default function CommercialGridDefaultsPanel() {
  const columns = useMemo(() => getProjectGridColumnDescriptors(), []);
  const [userTypes, setUserTypes] = useState(() => loadUserMatrixSettings().userTypes || []);
  const [enabled, setEnabled] = useState(false);
  const [rules, setRules] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const refreshUserTypes = useCallback(() => {
    setUserTypes(loadUserMatrixSettings().userTypes || []);
  }, []);

  useEffect(() => {
    refreshUserTypes();
    const onMatrix = () => refreshUserTypes();
    window.addEventListener("pb-user-matrix-settings-changed", onMatrix);
    return () => window.removeEventListener("pb-user-matrix-settings-changed", onMatrix);
  }, [refreshUserTypes]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const cfg = await fetchAppConfig();
      const normalized = normalizeCommercialGridDefaults(cfg?.commercialGridDefaults);
      setEnabled(normalized.enabled);
      setRules(rulesWithIds(normalized.rules));
      setRoleDefaults(roleDefaultsWithIds(normalized.roleDefaults, loadUserMatrixSettings().userTypes));
      setStatus("Loaded commercial table default filters.");
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Could not load default filters.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const addRule = useCallback(() => {
    setRules((prev) => [...prev, emptyRule(columns[0]?.colId || "status")]);
  }, [columns]);

  const removeRule = useCallback((id) => {
    setRules((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.id !== id)));
  }, []);

  const addRoleDefault = useCallback(() => {
    setRoleDefaults((prev) => [...prev, emptyRoleDefault(userTypes)]);
  }, [userTypes]);

  const updateRoleDefault = useCallback((id, patch) => {
    setRoleDefaults((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));
  }, []);

  const removeRoleDefault = useCallback((id) => {
    setRoleDefaults((prev) => prev.filter((entry) => entry.id !== id));
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    setError("");
    setStatus("");
    const payload = {
      enabled,
      rules: rules
        .map(({ colId, type, filter }) => ({
          colId: String(colId || "").trim(),
          type: String(type || "contains").trim(),
          filter: String(filter ?? "").trim(),
        }))
        .filter((r) => r.colId && r.filter),
      roleDefaults: roleDefaults
        .map(({ userTypeId, enabled: roleEnabled, rules: roleRules }) => ({
          userTypeId: String(userTypeId || "").trim(),
          enabled: Boolean(roleEnabled),
          rules: (roleRules || [])
            .map(({ colId, type, filter }) => ({
              colId: String(colId || "").trim(),
              type: String(type || "contains").trim(),
              filter: String(filter ?? "").trim(),
            }))
            .filter((r) => r.colId && r.filter),
        }))
        .filter((entry) => entry.userTypeId && entry.rules.length),
    };
    try {
      const cfg = await putAdminAppConfig({ commercialGridDefaults: payload });
      const normalized = normalizeCommercialGridDefaults(cfg?.commercialGridDefaults);
      hydrateCommercialGridDefaultsFromServer(normalized);
      window.dispatchEvent(new CustomEvent("pb-commercial-grid-defaults-changed"));
      setEnabled(normalized.enabled);
      setRules(rulesWithIds(normalized.rules));
      setRoleDefaults(roleDefaultsWithIds(normalized.roleDefaults, userTypes));
      setStatus("Saved commercial table default filters.");
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Could not save default filters.");
    } finally {
      setSaving(false);
    }
  }, [enabled, rules, roleDefaults, userTypes]);

  return (
    <section className="plugins-card plugins-card-wide email-triggers-panel" style={{ marginBottom: "1.5rem" }}>
      <h3 className="email-triggers-panel-title">Commercial table default filters</h3>
      <p className="email-triggers-panel-lead">
        When enabled, the Commercial board opens with these filters applied. Users can change or clear filters from
        the column headers, or use <strong>Reset filters</strong> on the board to restore these defaults.
      </p>

      {loading ? <p className="email-triggers-muted">Loading…</p> : null}
      {error ? (
        <p className="email-triggers-muted" role="alert">
          {error}
        </p>
      ) : null}
      {status ? (
        <p className="email-triggers-muted" role="status">
          {status}
        </p>
      ) : null}

      {!loading ? (
        <>
          <h4 className="email-triggers-panel-title" style={{ fontSize: "1rem", marginTop: "1rem" }}>
            Global defaults (all users)
          </h4>
          <label className="user-matrix-col-check" style={{ marginBottom: "0.75rem" }}>
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span>Apply global default filters when users open the Commercial table</span>
          </label>

          <FilterRulesTable
            columns={columns}
            rules={rules}
            onChange={setRules}
            onAdd={addRule}
            onRemove={removeRule}
          />

          <h4 className="email-triggers-panel-title" style={{ fontSize: "1rem", marginTop: "1.5rem" }}>
            Role-based defaults
          </h4>
          <p className="email-triggers-hint" style={{ marginBottom: "0.75rem" }}>
            Optional filters by User Matrix role. When enabled for a role, its rules are applied{" "}
            <strong>in addition to</strong> the global defaults above (for example, Drafters can default to Phase
            equals Drafting).
          </p>

          {roleDefaults.length === 0 ? (
            <p className="email-triggers-muted">No role-based defaults configured.</p>
          ) : (
            roleDefaults.map((entry) => (
              <div
                key={entry.id}
                className="plugins-card"
                style={{ marginBottom: "1rem", padding: "0.75rem 1rem" }}
              >
                <div
                  className="user-matrix-invite-row"
                  style={{ marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.75rem" }}
                >
                  <label className="user-matrix-col-check">
                    <input
                      type="checkbox"
                      checked={entry.enabled}
                      onChange={(e) => updateRoleDefault(entry.id, { enabled: e.target.checked })}
                    />
                    <span>Enabled</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span className="email-triggers-muted">User type</span>
                    <select
                      className="user-matrix-select"
                      value={entry.userTypeId}
                      onChange={(e) => updateRoleDefault(entry.id, { userTypeId: e.target.value })}
                      aria-label="User type"
                    >
                      {userTypes.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    className="email-triggers-remove"
                    onClick={() => removeRoleDefault(entry.id)}
                  >
                    Remove role
                  </button>
                </div>
                <FilterRulesTable
                  columns={columns}
                  rules={entry.rules}
                  onChange={(nextRules) => updateRoleDefault(entry.id, { rules: nextRules })}
                  onAdd={() =>
                    updateRoleDefault(entry.id, {
                      rules: [...entry.rules, emptyRule("phase")],
                    })
                  }
                  onRemove={(ruleId) =>
                    updateRoleDefault(entry.id, {
                      rules:
                        entry.rules.length <= 1
                          ? entry.rules
                          : entry.rules.filter((r) => r.id !== ruleId),
                    })
                  }
                  addLabel="Add rule for this role"
                />
              </div>
            ))
          )}

          <div className="user-matrix-invite-row" style={{ marginTop: "0.75rem" }}>
            <button
              type="button"
              className="email-triggers-button"
              onClick={addRoleDefault}
              disabled={!userTypes.length}
            >
              Add role default
            </button>
            <button
              type="button"
              className="email-triggers-button email-triggers-button-primary"
              onClick={save}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save default filters"}
            </button>
          </div>

          <p className="email-triggers-hint" style={{ marginTop: "0.75rem" }}>
            Example (global): two rules on <strong>Status</strong> — “Does not equal Complete” and “Does not equal
            Cancelled”. Example (role): for <strong>Drafter</strong>, one rule on <strong>Phase</strong> — “Equals
            Drafting”.
          </p>
        </>
      ) : null}
    </section>
  );
}
